/**
 * Privacy-safe, bounded diagnostics for one MatrixRTC/LiveKit call.
 *
 * Deliberately excluded: room/user/device identifiers, tokens/JWTs, URLs,
 * SDP/ICE data, Matrix event bodies and message content. Remote participants
 * are represented only by stable labels (participant-001, ...), scoped to
 * this in-memory log.
 */

export interface CallDiagnosticFields {
  participant?: string;
  state?: string;
  kind?: string;
  source?: string;
  muted?: boolean;
  error?: unknown;
  detail?: string;
  remoteParticipants?: number;
  remotePublications?: number;
  subscribedPublications?: number;
  remoteMemberships?: number;
  remoteLivekitTransports?: number;
  aliasMatches?: number;
  aliasDiffers?: number;
  aliasMissing?: number;
  serviceMatches?: number;
  serviceDiffers?: number;
}

type DiagnosticEntry = {
  at: string;
  event: string;
  fields?: Record<string, string | boolean | number>;
};

const MAX_ENTRIES = 300;
const MAX_VALUE_LENGTH = 160;
// Последний журнал переживает пересоздание call-hook при разморозке комнаты,
// но не перезагрузку страницы: это по-прежнему только память процесса.
let latestEntries: DiagnosticEntry[] = [];

function safeError(error: unknown): Record<string, string | number> {
  if (!error || typeof error !== "object") {
    return { name: "UnknownError" };
  }
  const candidate = error as {
    name?: unknown;
    code?: unknown;
    errcode?: unknown;
    httpStatus?: unknown;
    status?: unknown;
  };
  const result: Record<string, string | number> = {
    name:
      typeof candidate.name === "string" && /^[A-Za-z][A-Za-z0-9_.-]{0,63}$/.test(candidate.name)
        ? candidate.name
        : "Error",
  };
  const code = candidate.errcode ?? candidate.code;
  if (typeof code === "string" && /^[A-Z][A-Z0-9_.-]{0,63}$/.test(code)) result.code = code;
  if (typeof code === "number" && Number.isFinite(code)) result.code = code;
  const status = candidate.httpStatus ?? candidate.status;
  if (typeof status === "number" && Number.isFinite(status)) result.status = status;
  return result;
}

function safeToken(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  // Event fields are enums/short labels only. Reject identifiers, URLs,
  // whitespace-rich messages and opaque values instead of trying to redact
  // secrets after the fact.
  if (!/^[A-Za-z][A-Za-z0-9_.:-]{0,63}$/.test(value)) return undefined;
  if (value.includes("@") || value.includes("!") || value.includes("$") || value.includes("/")) return undefined;
  return value.slice(0, MAX_VALUE_LENGTH);
}

export class CallDiagnostics {
  private entries: DiagnosticEntry[] = [];
  private participantLabels = new Map<string, string>();

  public constructor(private readonly onChange: () => void) {}

  public reset(): void {
    this.entries = [];
    this.participantLabels.clear();
    this.add("diagnostics-started", { detail: "privacy-redacted" });
  }

  public participant(identity: string): string {
    let label = this.participantLabels.get(identity);
    if (!label) {
      label = `participant-${String(this.participantLabels.size + 1).padStart(3, "0")}`;
      this.participantLabels.set(identity, label);
    }
    return label;
  }

  public add(event: string, fields: CallDiagnosticFields = {}): void {
    const safeFields: Record<string, string | boolean | number> = {};
    const participant = safeToken(fields.participant);
    const state = safeToken(fields.state);
    const kind = safeToken(fields.kind);
    const source = safeToken(fields.source);
    const detail = safeToken(fields.detail);
    if (participant) safeFields.participant = participant;
    if (state) safeFields.state = state;
    if (kind) safeFields.kind = kind;
    if (source) safeFields.source = source;
    if (typeof fields.muted === "boolean") safeFields.muted = fields.muted;
    if (detail) safeFields.detail = detail;
    for (const key of [
      "remoteParticipants",
      "remotePublications",
      "subscribedPublications",
      "remoteMemberships",
      "remoteLivekitTransports",
      "aliasMatches",
      "aliasDiffers",
      "aliasMissing",
      "serviceMatches",
      "serviceDiffers",
    ] as const) {
      const value = fields[key];
      if (typeof value === "number" && Number.isSafeInteger(value) && value >= 0) {
        safeFields[key] = value;
      }
    }
    if (fields.error !== undefined) {
      const errorFields = safeError(fields.error);
      for (const [key, value] of Object.entries(errorFields)) safeFields[`error.${key}`] = value;
    }
    this.entries.push({
      at: new Date().toISOString(),
      event: safeToken(event) ?? "unknown-event",
      ...(Object.keys(safeFields).length > 0 ? { fields: safeFields } : {}),
    });
    if (this.entries.length > MAX_ENTRIES) {
      this.entries.splice(0, this.entries.length - MAX_ENTRIES);
    }
    latestEntries = [...this.entries];
    this.onChange();
  }

  public get hasEntries(): boolean {
    return this.entries.length > 0 || latestEntries.length > 0;
  }

  public text(): string {
    const entries = this.entries.length > 0 ? this.entries : latestEntries;
    return [
      "alg(success) call diagnostics",
      "Privacy: participant identities redacted; credentials, SDP/ICE and message/event bodies excluded.",
      `Exported: ${new Date().toISOString()}`,
      `Entries: ${entries.length}/${MAX_ENTRIES}`,
      "",
      ...entries.map((entry) => JSON.stringify(entry)),
    ].join("\n");
  }
}

/** Clipboard API first, with a legacy DOM fallback for older WebKit. */
export async function copyCallDiagnostics(text: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const area = document.createElement("textarea");
  area.value = text;
  area.readOnly = true;
  area.style.position = "fixed";
  area.style.opacity = "0";
  document.body.appendChild(area);
  area.select();
  try {
    if (!document.execCommand("copy")) throw new Error("ClipboardUnavailable");
  } finally {
    area.remove();
  }
}
